<template>
  <div class="multisteps-form__panel border-radius-xl bg-white" data-animation="FadeIn">
    <h5 class="font-weight-bolder">{{ $t('location.Contact_address_details') }}</h5>
    <p class="mb-0 text-sm">{{ $t('location.Mandatory_informations') }} *</p>
    <div class="multisteps-form__content">
      <div class="row mt-4">
        <div class="col-6">
          <div class="input-group input-group-static null">
            <label class="">{{ $t('location.E-mail') }}</label>
            <input v-model="localFormData.email" type="text" class="form-control form-control-default" :placeholder="$t('location.E-mail')">            
          </div>  
        </div>               
        <div class="col-6">
          <div class="input-group input-group-static null">
            <label class="">{{ $t('location.Phone_number') }}</label>
            <input v-model="localFormData.phoneNumber" type="text" class="form-control form-control-default" :placeholder="$t('location.Phone_number')">            
          </div>
        </div>       
      </div>
      <div class="row mt-4">
        <div class="col-8">
          <div class="input-group input-group-static null">
            <label class="">{{ $t('location.Address') }} *</label>
            <input v-model="localFormData.street" type="text" class="form-control form-control-default" :placeholder="$t('location.Address')">
            <span v-if="v$.street.$error" class="text-sm text-danger">
              {{ v$.street.$errors[0].$message }}
            </span>
          </div>
        </div>
        <div class="col-4">
          <div class="input-group input-group-static null">
            <label class="">{{ $t('location.House_number') }} *</label>
            <input v-model="localFormData.houseNumber" type="text" class="form-control form-control-default" :placeholder="$t('location.House_number')">
            <span v-if="v$.houseNumber.$error" class="text-sm text-danger">
              {{ v$.houseNumber.$errors[0].$message }}
            </span>
          </div>
        </div>
      </div>
      <div class="row mt-4">
        <div class="col-4">
          <div class="input-group input-group-static null">
            <label class="">{{ $t('location.Zipcode') }} *</label>
            <input v-model="localFormData.postalCode" type="text" class="form-control form-control-default" :placeholder="$t('location.Zipcode')">
            <span v-if="v$.postalCode.$error" class="text-sm text-danger">
              {{ v$.postalCode.$errors[0].$message }}
            </span>
          </div>
        </div>
        <div class="col-4">
          <div class="input-group input-group-static null">
            <label class="">{{ $t('location.City') }} *</label>
            <input v-model="localFormData.place" type="text" class="form-control form-control-default" :placeholder="$t('location.City')">
            <span v-if="v$.place.$error" class="text-sm text-danger">
              {{ v$.place.$errors[0].$message }}
            </span>
          </div>
        </div>
        <div class="col-4">
          <div class="input-group input-group-static null">
            <label class="">{{ $t('location.Country') }} *</label>
            <input v-model="localFormData.country" type="text" class="form-control form-control-default" :placeholder="$t('location.Country')">
            <span v-if="v$.country.$error" class="text-sm text-danger">
              {{ v$.country.$errors[0].$message }}
            </span>
          </div>
        </div>
      </div>
      <div class="row mt-4">       
        <div class="col-4">
          <div class="input-group input-group-static null">
            <label class="">{{ $t('location.Laitiude') }}</label>
            <input v-model="localFormData.latitude" type="text" class="form-control form-control-default" :placeholder="$t('location.Laitiude')">
            <span v-if="v$.latitude.$error" class="text-sm text-danger">
              {{ v$.latitude.$errors[0].$message }}
            </span>
          </div>
        </div>      
        <div class="col-4">
          <div class="input-group input-group-static null">
            <label class="">{{ $t('location.Longitude') }}</label>
            <input v-model="localFormData.longitude" type="text" class="form-control form-control-default" :placeholder="$t('location.Longitude')">
            <span v-if="v$.longitude.$error" class="text-sm text-danger">
              {{ v$.longitude.$errors[0].$message }}
            </span>            
          </div>
        </div>        
      </div>
      <div class="button-row d-flex mt-4">
        <button class="btn mb-0 bg-gradient-light btn-sm null null js-btn-prev" type="button" @click="this.$parent.prevStep">{{ $t('common.Prev') }}</button>
        <button class="btn mb-0 btn-sm bg-gradient-success null null ms-auto js-btn-next" type="button" @click="validateAndNextStep">{{ $t('common.Next') }}</button>
      </div>
    </div>
  </div>
</template>

<script>
  import useVuelidate from '@vuelidate/core'
  import { required, email as emailValidator, helpers } from '@vuelidate/validators'
  import { computed, reactive, watch } from 'vue'
  import { useI18n } from 'vue-i18n'

export default {
  name: "AddressSection",
  props: {
    formData: Object,
    nextStep: Function
  },
  setup(props, { emit }) {
    const { t } = useI18n()
    const localFormData = reactive({ ...props.formData })
    const isLatitude = helpers.withMessage(
      t('location.Latitude_range'),
      value => {
        if (value === '' || value === null || value === undefined) return true;
        // Match only valid numbers (integer or decimal, optional sign)
        const numberRegex = /^-?\d+(\.\d+)?$/;
        if (!numberRegex.test(value)) return false;

        const num = parseFloat(value);
        return num >= -90 && num <= 90;
      }
    );

    const isLongitude = helpers.withMessage(
      t('location.Longitude_range'),
      value => {
        if (value === '' || value === null || value === undefined) return true;
        const numberRegex = /^-?\d+(\.\d+)?$/;
        if (!numberRegex.test(value)) return false;

        const num = parseFloat(value);
        return num >= -180 && num <= 180;
      }
    );

    const bothOrNone = helpers.withMessage(
      t('common.LatitudeLongitudePair'),
      helpers.withParams({ type: 'bothOrNone' }, (value, siblings) => {
        const lat = siblings.latitude;
        const lng = siblings.longitude;
        return (lat !== '' && lng !== '') || (lat === '' && lng === '');
      })
    );

    const rules = computed(() => ({
      street: { required: helpers.withMessage(t('common.Required'), required) },
      houseNumber: { required: helpers.withMessage(t('common.Required'), required) },
      place: { required: helpers.withMessage(t('common.Required'), required) },
      postalCode: { required: helpers.withMessage(t('common.Required'), required) },
      country: { required: helpers.withMessage(t('common.Required'), required) },
      email: { 
        email: helpers.withMessage(t('common.Invalid_email_address'), emailValidator) 
      },
      latitude: {
        isLatitude,
        bothOrNone
      },
      longitude: {
        isLongitude,
        bothOrNone
      }
    }))
    
    const v$ = useVuelidate(rules, localFormData)

    watch(localFormData, () => {
      emit('update:formData', localFormData)
    }, { deep: true })

    const validateAndNextStep = async () => {

      console.log(localFormData.latitude);
      console.log(localFormData.longitude);

      v$.value.$touch() // Mark all fields as touched
      if (v$.value.$pending) {
        await v$.value.$validate() // Ensure validation completes
      }
      if (!v$.value.$invalid) {
        props.nextStep()
      }
    }

    return {
      localFormData,
      v$,
      validateAndNextStep
    }
  }
};
</script>